import { GET_AUTOMATION_USE_CASES } from "../AutomationsAPI";
import { useQuery } from "@apollo/client";
import "./AddAutomation.scss";
import { useMemo, useContext } from "react";
import AddAutomationSection, {
  type onCardClickType,
} from "./AddAutomationSection";
import { ticketEndpointTypeEnum } from "@SeemplicityDev/ticket-master";
import {
  Loader,
  useIcon,
  ModalContext,
  type ModalContextType,
} from "@SeemplicityDev/shared";
import { useHistory } from "react-router-dom";
import { AutomationTypeEnum } from "../AutomationsUtils";
import { useAutomationRouteConfig } from "../useAutomationRouteConfig";
import { AUTOMATION_TITLE_PARAM } from "../AutomationsUseCases/useAutomationWizardName/useAutomationWizardName";

const automationTypesMap = {
  [ticketEndpointTypeEnum.TICKET]: {
    label: "Ticketing",
    showEmptyState: true,
  },
  [ticketEndpointTypeEnum.REPORT]: {
    label: "Reports",
  },
  [ticketEndpointTypeEnum.INTERNAL]: {
    label: "Views",
    showIntegrations: false,
  },
};

export type AutomationUseCase = {
  selection_rule?: {
    filtersid?: string;
  } | null;
  id: string;
  endpoint_type: string;
  title: string;
  icon: string;
  description: string;
  labels: string[];
  integrations: {
    friendly_name: string;
    type: string;
  }[];
  valid: boolean;
  automation_type: AutomationTypeEnum;
};

export type AddAutomationProps = {
  selectedFilter?: {
    label?: string;
    value: {
      query?: string;
      id?: string;
    };
  };
  selectedScope?: {
    label?: string;
    value: {
      query?: string;
      id?: string;
    };
  };
};

const AddAutomation: React.FC<AddAutomationProps> = ({
  selectedFilter,
  selectedScope,
}) => {
  const history = useHistory();
  const { closeModal } = useContext(ModalContext) as ModalContextType;
  const { getSvg } = useIcon();
  const { automationsUseCases } = useAutomationRouteConfig();
  const { data: automationUseCases, loading } = useQuery(
    GET_AUTOMATION_USE_CASES
  );

  const categorizedUseCases = useMemo(() => {
    return automationUseCases?.automation_use_cases?.reduce(
      (
        acc: Record<string, AutomationUseCase[]>,
        useCase: AutomationUseCase
      ) => {
        const type = useCase.endpoint_type;
        if (!acc[type]) {
          acc[type] = [];
        }

        acc[type].push(useCase);
        return acc;
      },
      {}
    );
  }, [automationUseCases]);

  const handleCardClick: onCardClickType = (
    integrationType,
    automationType,
    automationId
  ) => {
    const params = new URLSearchParams();

    const selectedAutomation = automationUseCases?.automation_use_cases?.find(
      (automation: AutomationUseCase) => automation.id === automationId
    );

    const filtersid =
      selectedAutomation?.selection_rule?.filtersid ||
      selectedFilter?.value?.id;

    if (filtersid) {
      params.set("filtersid", filtersid);
    }

    if (selectedScope?.value?.id) {
      params.set("scopesid", selectedScope.value.id);
    }

    if (selectedFilter?.value?.query) {
      params.set("filtersjson", selectedFilter.value.query);
    }

    if (selectedFilter?.label) {
      params.set("filterslabel", selectedFilter.label);
    }

    const automationRoute = automationsUseCases?.find(
      (automation) => automation.type === automationType
    )?.route;

    if (!!automationType && automationRoute) {
      if (selectedAutomation?.title) {
        params.set(AUTOMATION_TITLE_PARAM, selectedAutomation.title);
      }

      history.push({
        pathname: `/remediation/${automationRoute}/new`,
        search: params.toString(),
      });
      closeModal();
      return;
    }

    if (integrationType) {
      params.set("integration_type", integrationType);
    }

    history.push({
      pathname: "/remediationQueue",
      search: params.toString(),
    });

    closeModal();
  };

  return (
    <div
      className={`add-automation ${loading ? "is-loading" : ""}`}
      data-testid="add-automation-modal"
    >
      <div className="add-automation-description">
        Choose the automation that works for you, whether it's opening tickets,
        or sending reports and insights that spark action.
      </div>
      {!loading ? (
        Object.entries(automationTypesMap)?.map(
          ([type, { label, showEmptyState, showIntegrations }]) => (
            <AddAutomationSection
              key={type}
              label={label}
              showEmptyState={showEmptyState}
              showIntegrations={showIntegrations}
              useCases={categorizedUseCases?.[type] || []}
              onCardClick={handleCardClick}
            />
          )
        )
      ) : (
        <Loader
          center
          img={getSvg("icon-spinner")}
          fullHeight
          iconSize={"xxxxl"}
        />
      )}
    </div>
  );
};

export default AddAutomation;
